import React, { useState } from 'react'
import useModals from '@/v2/hooks/useModals'
import GalleryItem from '@/v2/components/common/GalleryItem'
import Typography from '@/v2/components/common/Typography'
import Document from '#/assets/images/v2/document.jpg'
import { DATE_FORMAT, CONTRACT_TREATMENT_TYPE_LABELS } from '@/v2/constants'
import { getContractStatus } from '@/v2/utils/helpers'
import { getDateTimeWithoutTimezone, safeFormat } from '@/v2/utils/convert'
import {
  useArchiveContract,
  useSendContract,
} from '@/v2/hooks/documents/useContracts'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import Dialog from '@mui/material/Dialog'
import { Grid } from '@mui/material'
import Button from '@/v2/components/common/Button'

const Contract = ({ contract, patientId }) => {
  const { goTo } = useModals()
  const { archiveContract } = useArchiveContract(patientId, contract.id)
  const { sendContract } = useSendContract(contract.id)
  const { onOpenSnackbar } = useSnackbarContext()
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false)

  if (!contract) return null

  const handleResendEmail = async () => {
    try {
      const { error } = await sendContract()

      if (!error) {
        onOpenSnackbar('Email sent successfully', 'success')
      }
    } catch (error) {
      onOpenSnackbar('Something went wrong.', 'error')
    }
  }

  const handleArchiveClick = () => {
    if (contract.paymentPlan?.status === 'on_going') {
      setOpenArchiveConfirmation(true)
    } else {
      handleArchive()
    }
  }

  const handleArchive = async () => {
    try {
      const { error } = await archiveContract()

      if (!error) {
        onOpenSnackbar('Contract archived successfully', 'success')
      }
    } catch (error) {
      onOpenSnackbar('Something went wrong.', 'error')
    } finally {
      setOpenArchiveConfirmation(false)
    }
  }

  const contractStatus = getContractStatus(contract)

  // The details modal reads the live record from the ['Contracts', patientId]
  // query; without patientId it is stuck with this snapshot.
  const viewOption = {
    label: 'View',
    onClick: () =>
      goTo(`/v2/contract-details/${contract.id}`, {
        initialValues: { contract, patientId },
      }),
  }

  const contractOptions = !contract.active
    ? [viewOption]
    : [
        viewOption,
        contract?.signed
          ? {
              label: 'Download PDF',
              href: `/contracts/${contract.id}/pdf`,
              target: '_blank',
            }
          : {
              label: contract?.emailSent ? 'Resend email' : 'Send email',
              onClick: handleResendEmail,
            },
        {
          label: 'Archive',
          onClick: handleArchiveClick,
        },
      ]

  const createdAt = getDateTimeWithoutTimezone(contract.createdAt)

  return (
    <>
      <GalleryItem
        preview={Document}
        contractStatus={contractStatus}
        options={contractOptions}
      >
        <Typography fontWeight="500" fontSize="16px">
          Treatment Contract {`#${contract.identifierToken}`}
        </Typography>
        <Typography fontSize="14px">
          {safeFormat(createdAt, DATE_FORMAT)}
        </Typography>
        <Typography fontSize="14px">
          {CONTRACT_TREATMENT_TYPE_LABELS[contract.treatmentType]}
        </Typography>
      </GalleryItem>

      <Dialog
        open={openArchiveConfirmation}
        onClose={() => setOpenArchiveConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <div style={{ padding: '38px 30px 23px 30px', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ marginBottom: '24px' }}>
            Are you sure you want to archive this contract?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '24px', color: 'text.secondary' }}
          >
            This contract has an ongoing payment plan. Archiving it may affect
            the payment plan processing.
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenArchiveConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleArchive}
              sx={{
                color: '#fff',
              }}
            >
              Archive
            </Button>
          </Grid>
        </div>
      </Dialog>
    </>
  )
}

export default Contract
